import { ActionSheetContext, Toast, ToastContext } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { CheckoutFlowTypeMap } from '@alto/scriptdash/alto/patient_app/checkout_flow/types/v1/checkout_flow_type';
import { OrderBundlingOriginMap } from '@alto/scriptdash/alto/patient_app/scheduling/order_bundling/types/v1/order_bundling_origin';
import React, { useCallback, useContext } from 'react';
import { fetchEnterCheckoutSucceeded } from '~shared/actions/checkout';
import { useCartUsers } from '~shared/features/cart/hooks/useCartUsers';
import { getDefaultAddress } from '~shared/features/checkout/selectors/getDefaultAddress';
import { Sentry } from '~shared/sentry';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OrderBundlingActionSheet, useOrderBundlingOptions } from '../../order-bundling';
import { EarliestDateCompoundActionSheet } from '../components/earliest-date/EarliestDateCompoundActionSheet';
import { useEnterCheckoutFlow } from './useEnterCheckoutFlow';
import { useInitializeNewOrder } from './useInitializeNewOrder';

export const CHECKOUT_STEPS = {
  CART: 'cart',
  EARLIEST_DATE_COMPOUND: 'earliest_date_compound',
  PROGYNY_WIZARD: 'progyny_wizard',
  PHOTO_ID_UPLOAD: 'photo_id_upload',
  ORDER_BUNDLING: 'order_bundling',
};

export type CheckoutStepType = (typeof CHECKOUT_STEPS)[keyof typeof CHECKOUT_STEPS];

export const useCheckoutInterstitials = () => {
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { navigate, replace } = useNavigation();
  const { isPhotoIDRequired: showPhotoIDInterstitial } = useCartUsers();
  const address = useSelectorShared(getDefaultAddress);
  const { earliestUpcomingShipment: upcomingBundleEligibleShipment } = useOrderBundlingOptions({
    origin: OrderBundlingOriginMap.ENTER_CHECKOUT,
    address_id: address?.id,
  });
  const { flowData, flowTypes, refetch } = useEnterCheckoutFlow({ enabled: false });
  const { initializeCartItems } = useInitializeNewOrder();
  const dispatch = useDispatchShared();

  const navigateOrShowBundleUpsell = useCallback(
    (step?: CheckoutStepType) => {
      if (upcomingBundleEligibleShipment) {
        setActiveActionSheet(<OrderBundlingActionSheet />);

        return;
      }

      if (step === CHECKOUT_STEPS.PHOTO_ID_UPLOAD) {
        replace('RouteCheckout');

        return;
      }

      navigate('RouteCheckout');
    },
    [navigate, upcomingBundleEligibleShipment, replace, setActiveActionSheet],
  );

  // This function is to be called once a step in the "enter checkout" flow has been completed.
  // It does two things:
  // 1. Populate the react query cache with the response from /enter_checkout.
  // 2. Use the data from the response and other sources to determine where to navigate the user next.
  return useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (step: CheckoutStepType) => {
      // If we completed the first step, CHECKOUT_STEP.CART, flowTypes/Data will most likely be undefined in which case we need to make a query.
      // There is a possibility that a user can complete cart, navigates to checkout/any interstitial, and navigates back to cart.
      // In this case we'd want to use the data from the cache instead of making a query.
      let data = {
        flowData,
        flowTypes,
      };

      if (!flowTypes) {
        try {
          const { data: responseData } = await refetch({ throwOnError: true });

          if (responseData?.error) {
            addToast(<Toast variant="error">{responseData.error}</Toast>);
            return;
          }

          // Even though we migrated the call to enter_checkout from redux to react query, there are other parts of the app that depend on the response data to be present in the redux store (progyny).
          // Refactoring those components to use the query cache is out of scope at the moment, so in the meantime we are still going to populate redux store with the response from enter_checkout.
          if (responseData?.flowTypes) {
            dispatch(
              fetchEnterCheckoutSucceeded({
                checkout_flow_types: responseData.flowTypes,
                checkout_flow_data: responseData.flowData,
              }),
            );
            data = { flowData: responseData.flowData, flowTypes: responseData.flowTypes };
          }
        } catch (e) {
          Sentry.captureException(e);
          const error = 'There was a problem with your request, please try again.';
          addToast(<Toast variant="error">{error}</Toast>);
          return;
        }
      }

      const checkoutFlowTypes = data?.flowTypes;

      switch (step) {
        case CHECKOUT_STEPS.CART: {
          // load server-side cart into redux
          initializeCartItems();
          const showCompoundInterstitial = !!checkoutFlowTypes?.includes(CheckoutFlowTypeMap.EARLIEST_DATE_COMPOUND);
          if (showCompoundInterstitial) {
            setActiveActionSheet(<EarliestDateCompoundActionSheet />);
            return;
          }

          const showProgynyInterstitial = !!checkoutFlowTypes?.includes(CheckoutFlowTypeMap.PROGYNY_WIZARD);
          if (showProgynyInterstitial) {
            navigate('RouteProgynyWizardIntro');
            return;
          }

          if (showPhotoIDInterstitial) {
            navigate('RouteCheckoutPhotoIdUpload');
            return;
          }

          break;
        }

        case CHECKOUT_STEPS.EARLIEST_DATE_COMPOUND: {
          const showProgynyInterstitial = !!checkoutFlowTypes?.includes(CheckoutFlowTypeMap.PROGYNY_WIZARD);
          if (showProgynyInterstitial) {
            navigate('RouteProgynyWizardIntro');
            return;
          }

          if (showPhotoIDInterstitial) {
            navigate('RouteCheckoutPhotoIdUpload');
            return;
          }

          break;
        }

        case CHECKOUT_STEPS.PROGYNY_WIZARD: {
          if (showPhotoIDInterstitial) {
            navigate('RouteCheckoutPhotoIdUpload');
            return;
          }

          break;
        }

        case CHECKOUT_STEPS.PHOTO_ID_UPLOAD:
        default:
          break;
      }

      navigateOrShowBundleUpsell(step);
    },
    [
      flowData,
      flowTypes,
      navigateOrShowBundleUpsell,
      refetch,
      dispatch,
      addToast,
      showPhotoIDInterstitial,
      navigate,
      setActiveActionSheet,
      initializeCartItems,
    ],
  );
};
